import { useState } from 'react'
import { apiPost, ApiError } from '../lib/api.ts'

/**
 * Bulk import of contact results from a file (docs/campaign-workflow.md).
 *
 * THE FILE IS PARSED HERE, in the browser. That keeps a multipart dependency
 * off the server, lets the uploader map columns and see what will happen before
 * anything is sent, and means the bytes of a campaign's spreadsheet never leave
 * the machine — only the rows do.
 *
 * CSV and XLSX. The XLSX reader is loaded ON DEMAND, only when someone picks an
 * .xlsx file, so the parser is not in the bundle of everyone who never imports
 * one — the same reasoning as the map route.
 *
 * `read-excel-file`, not SheetJS. The `xlsx` package on npm is frozen at 0.18.5,
 * the last release before SheetJS moved distribution to their own CDN, and it
 * carries a prototype-pollution CVE fixed only in versions npm does not have.
 * This code parses files a campaign was handed by a third party, in the
 * uploader's browser, so an unmaintained parser with a known CVE is not a
 * reasonable thing to reach for.
 *
 * THE RAGGED-ROW TRAP (docs/list-formats.md): a blank cell is OMITTED from the
 * XLSX XML entirely, so a parser that walks the cells that exist shifts every
 * later value one column left — silently, and only on the rows that have a
 * gap. Verified against a hand-built file whose row 3 omits cell B3: the reader
 * returns `null` in that position and holds the alignment.
 *
 * TWO PHASES, because an import rewrites a campaign's contact history: upload
 * shows how many rows match and which do not, and nothing is recorded until
 * Commit.
 */

interface Report {
  import_id: string
  filename: string | null
  status: string
  row_count: number
  resolved: string
  unresolved: string
  problems: { row_no: number; raw_match: string; raw_outcome: string; error: string }[]
}

/**
 * RFC 4180, enough of it.
 *
 * Quoted fields, embedded commas and newlines, and doubled quotes — which is
 * what a spreadsheet actually emits. Hand-written rather than a dependency: it
 * is thirty lines, and the alternative is a package in the bundle for one
 * screen.
 */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let quoted = false

  // Normalise line endings first so CRLF does not leave \r on every last field.
  const input = text.replace(/\r\n?/g, '\n')

  for (let i = 0; i < input.length; i++) {
    const c = input[i]!
    if (quoted) {
      if (c === '"') {
        if (input[i + 1] === '"') {
          field += '"'
          i++
        } else quoted = false
      } else field += c
    } else if (c === '"') {
      quoted = true
    } else if (c === ',') {
      row.push(field)
      field = ''
    } else if (c === '\n') {
      row.push(field)
      rows.push(row)
      row = []
      field = ''
    } else {
      field += c
    }
  }
  if (field !== '' || row.length > 0) {
    row.push(field)
    rows.push(row)
  }
  // A trailing newline leaves one empty row; a file of blank lines leaves many.
  return rows.filter((r) => r.some((cell) => cell.trim() !== ''))
}

/**
 * Hashes the file's BYTES, not its text.
 *
 * Uniform across CSV and XLSX, and it is the file's own identity that makes a
 * re-upload record nothing — decoding first would make two different files with
 * the same text collide, and would not work for a binary format at all.
 */
async function sha256(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * One spreadsheet cell as the text the importer sends.
 *
 * A date arrives as a real Date, built at UTC midnight by the reader, so its
 * UTC parts are the date the file meant — reading local parts would shift it a
 * day for anyone west of Greenwich. Formatted as YYYY-MM-DD because that is one
 * of the three shapes `canonical.norm_date` accepts.
 */
export function cellToText(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (value instanceof Date) return value.toISOString().slice(0, 10)
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return String(value).trim()
}

/** Rows from an .xlsx, normalised to the same shape parseCsv returns. */
async function parseXlsx(file: File): Promise<string[][]> {
  // Dynamic: the reader and its unzip/XML dependencies stay out of the shared
  // bundle until somebody actually picks a spreadsheet.
  const { default: readXlsx } = await import('read-excel-file/browser')
  const result = (await readXlsx(file as never)) as unknown

  // The reader returns either rows, or one entry per sheet carrying them.
  // Handled both ways rather than pinned to one, because which it is depends on
  // options and version and getting it wrong is a blank import.
  const first = Array.isArray(result) ? result[0] : undefined
  const rows =
    first && !Array.isArray(first) && typeof first === 'object' && 'data' in first
      ? ((first as { data: unknown[][] }).data ?? [])
      : ((result as unknown[][]) ?? [])

  return rows
    .map((row) => (Array.isArray(row) ? row.map(cellToText) : []))
    .filter((row) => row.some((cell) => cell !== ''))
}

const FIELDS = [
  { key: 'match', label: 'Voter', required: true, hint: 'the barcode row code, or the state ID' },
  { key: 'outcome', label: 'Outcome', required: true, hint: 'SUP, not_home, …' },
  { key: 'when', label: 'Date', required: false, hint: 'when the contact happened' },
  { key: 'canvasser', label: 'Canvasser', required: false, hint: 'who did it' },
  { key: 'notes', label: 'Notes', required: false, hint: '' },
] as const

const selectClass =
  'rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-ink'

export function ImportContacts({ campaignId }: { campaignId: string }) {
  const [rows, setRows] = useState<string[][] | null>(null)
  const [filename, setFilename] = useState('')
  const [checksum, setChecksum] = useState('')
  const [mapping, setMapping] = useState<Record<string, number>>({})
  const [matchBy, setMatchBy] = useState<'row_code' | 'registrant_id'>('row_code')
  const [method, setMethod] = useState('walk')
  const [report, setReport] = useState<Report | null>(null)
  const [committed, setCommitted] = useState<{ recorded: string; skipped: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function onFile(file: File) {
    setError(null)
    setReport(null)
    setCommitted(null)
    setBusy(true)
    let parsed: string[][]
    try {
      const bytes = await file.arrayBuffer()
      // The checksum is of the FILE, not of what we made of it — it is what
      // makes re-uploading the same spreadsheet a no-op rather than a double
      // count.
      setChecksum(await sha256(bytes))
      parsed = /\.xlsx$/i.test(file.name)
        ? await parseXlsx(file)
        : parseCsv(new TextDecoder().decode(bytes))
    } catch {
      setError('That file could not be read. If it is an .xls or .numbers file, save it as .xlsx or CSV.')
      setBusy(false)
      return
    } finally {
      setBusy(false)
    }

    if (parsed.length < 2) {
      setError('That file has no rows under its header.')
      return
    }
    setRows(parsed)
    setFilename(file.name)

    // A first guess at the mapping from the header names. Only a guess: the
    // dropdowns are the truth, and a wrong guess is one click to fix.
    const header = parsed[0]!.map((h) => h.trim().toLowerCase())
    const guess: Record<string, number> = {}
    const find = (...names: string[]) => header.findIndex((h) => names.some((n) => h.includes(n)))
    for (const [key, ...names] of [
      ['match', 'code', 'barcode', 'voter', 'registrant', 'id'],
      ['outcome', 'outcome', 'result', 'disposition', 'response'],
      ['when', 'date', 'when'],
      ['canvasser', 'canvasser', 'walker', 'volunteer'],
      ['notes', 'note', 'comment'],
    ] as string[][]) {
      const at = find(...names.slice(0))
      if (at >= 0) guess[key!] = at
    }
    setMapping(guess)
  }

  async function upload() {
    if (!rows) return
    setBusy(true)
    setError(null)
    try {
      const body = {
        campaign_id: campaignId,
        filename,
        checksum,
        match_by: matchBy,
        method,
        // Row 1 is the header; the file's own row numbers start at 2, and
        // keeping them means a reported problem points at the line the
        // uploader can actually find in their spreadsheet.
        rows: rows.slice(1).map((r, i) => ({
          row_no: i + 2,
          match: (r[mapping['match'] ?? -1] ?? '').trim(),
          outcome: (r[mapping['outcome'] ?? -1] ?? '').trim(),
          ...(mapping['when'] !== undefined ? { when: (r[mapping['when']] ?? '').trim() } : {}),
          ...(mapping['canvasser'] !== undefined
            ? { canvasser: (r[mapping['canvasser']] ?? '').trim() }
            : {}),
          ...(mapping['notes'] !== undefined ? { notes: (r[mapping['notes']] ?? '').trim() } : {}),
        })),
      }
      setReport(await apiPost<Report>('/contacts/import', body))
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That file could not be read.')
    } finally {
      setBusy(false)
    }
  }

  async function commit() {
    if (!report) return
    setBusy(true)
    try {
      setCommitted(
        await apiPost<{ recorded: string; skipped: string }>(
          `/contacts/import/${report.import_id}/commit`,
        ),
      )
      setReport(null)
      setRows(null)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That import could not be committed.')
    } finally {
      setBusy(false)
    }
  }

  const header = rows?.[0] ?? []
  const ready = mapping['match'] !== undefined && mapping['outcome'] !== undefined

  return (
    <section className="mt-10 border-t border-line pt-6">
      <h2 className="text-sm font-semibold">Import results from a file</h2>
      <p className="mt-1 text-xs text-ink-muted">
        For results collected somewhere else — another canvassing tool, a phone vendor&rsquo;s
        export, a spreadsheet someone kept. CSV or Excel (.xlsx).
      </p>

      <div className="mt-3">
        <input
          type="file"
          accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) void onFile(file)
          }}
          className="text-sm"
        />
      </div>

      {error ? (
        <p role="alert" className="mt-3 rounded-md border border-gold-500 bg-surface-sunken px-3 py-2 text-sm text-ink">
          {error}
        </p>
      ) : null}

      {committed ? (
        <p role="status" className="mt-3 rounded-md border border-line bg-surface-sunken px-3 py-2 text-sm text-ink">
          Recorded <strong>{committed.recorded}</strong> contacts.
          {Number(committed.skipped) > 0 ? (
            <>
              {' '}
              <strong>{committed.skipped}</strong> were already on record from this same file — a
              re-upload records nothing twice.
            </>
          ) : null}
        </p>
      ) : null}

      {rows && !report ? (
        <>
          <p className="mt-4 text-sm">
            <strong>{(rows.length - 1).toLocaleString()}</strong> rows in{' '}
            <span className="font-mono text-xs">{filename}</span>. Which column is which?
          </p>

          <div className="mt-3 flex flex-wrap gap-3">
            {FIELDS.map((f) => (
              <label key={f.key} className="text-sm">
                <span className="mb-1 block text-xs font-medium tracking-wide text-ink-muted uppercase">
                  {f.label}
                  {f.required ? '' : ' (optional)'}
                </span>
                <select
                  className={selectClass}
                  value={mapping[f.key] ?? ''}
                  onChange={(e) =>
                    setMapping((m) => {
                      const next = { ...m }
                      if (e.target.value === '') delete next[f.key]
                      else next[f.key] = Number(e.target.value)
                      return next
                    })
                  }
                >
                  <option value="">—</option>
                  {header.map((h, i) => (
                    <option key={i} value={i}>
                      {h || `column ${i + 1}`}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>

          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="text-sm">
              <span className="mb-1 block text-xs font-medium tracking-wide text-ink-muted uppercase">
                The voter column holds
              </span>
              <select
                className={selectClass}
                value={matchBy}
                onChange={(e) => setMatchBy(e.target.value as 'row_code' | 'registrant_id')}
              >
                <option value="row_code">a barcode row code from our sheets</option>
                <option value="registrant_id">a state registrant ID</option>
              </select>
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-xs font-medium tracking-wide text-ink-muted uppercase">
                How they were contacted
              </span>
              <select className={selectClass} value={method} onChange={(e) => setMethod(e.target.value)}>
                <option value="walk">Canvass</option>
                <option value="phone">Phone</option>
                <option value="text">Text</option>
                <option value="event">Event</option>
                <option value="other">Other</option>
              </select>
            </label>
            <button
              type="button"
              disabled={!ready || busy}
              onClick={() => void upload()}
              className="rounded-md bg-brand-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
            >
              {busy ? 'Checking…' : 'Check the file'}
            </button>
          </div>
        </>
      ) : null}

      {report ? (
        <div className="mt-4 rounded-lg border border-line bg-surface-sunken p-4">
          <p className="text-sm">
            <strong className="text-lg tabular-nums">{Number(report.resolved).toLocaleString()}</strong>{' '}
            of {report.row_count.toLocaleString()} rows matched a voter and a known outcome.
            {Number(report.unresolved) > 0 ? (
              <>
                {' '}
                <strong className="tabular-nums">{Number(report.unresolved).toLocaleString()}</strong>{' '}
                did not, and will be skipped.
              </>
            ) : null}
          </p>
          <p className="mt-1 text-xs text-ink-muted">Nothing has been recorded yet.</p>

          {report.problems.length > 0 ? (
            <table className="mt-3 w-full border-collapse text-xs">
              <thead>
                <tr className="border-b border-line text-left text-ink-muted">
                  <th scope="col" className="py-1 pr-3 font-semibold">Row</th>
                  <th scope="col" className="py-1 pr-3 font-semibold">Voter</th>
                  <th scope="col" className="py-1 pr-3 font-semibold">Outcome</th>
                  <th scope="col" className="py-1 font-semibold">Why</th>
                </tr>
              </thead>
              <tbody>
                {report.problems.map((p) => (
                  <tr key={p.row_no} className="border-b border-line/60">
                    <td className="py-1 pr-3 tabular-nums">{p.row_no}</td>
                    <td className="py-1 pr-3 font-mono">{p.raw_match}</td>
                    <td className="py-1 pr-3 font-mono">{p.raw_outcome}</td>
                    <td className="py-1 text-ink-muted">{p.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}

          <div className="mt-4 flex gap-3">
            <button
              type="button"
              disabled={busy || Number(report.resolved) === 0}
              onClick={() => void commit()}
              className="rounded-md bg-brand-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
            >
              {busy ? 'Recording…' : `Record ${Number(report.resolved).toLocaleString()} contacts`}
            </button>
            <button
              type="button"
              onClick={() => {
                setReport(null)
                setRows(null)
              }}
              className="text-xs text-ink-muted hover:text-ink"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </section>
  )
}
